import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Users2,
  Trophy,
  AlertTriangle,
  Wallet,
  TrendingUp,
  CalendarDays,
  Search,
  X,
  Filter,
  Download,
} from "lucide-react";

/**
 * ReferralsTab · Programa de Referidos (panel global).
 *
 * Backend routes:
 *   GET /api/referrals
 *   GET /api/referrals/alerts
 */

const STATUS_META = {
  sin_gestionar:       { label: "Sin gestionar",              cls: "bg-neutral-100 text-neutral-700" },
  pendiente_contacto:  { label: "Pendiente contacto",         cls: "bg-amber-100 text-amber-800" },
  pendiente_bizum:     { label: "Pendiente conf. Bizum",       cls: "bg-orange-100 text-orange-800" },
  pago_pendiente:      { label: "Pago pendiente",              cls: "bg-red-100 text-red-800" },
  pagado:              { label: "Pagado",                      cls: "bg-emerald-100 text-emerald-800" },
};


function KpiCard({ icon: Icon, label, value, sub, color = "text-neutral-900", testid }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
}


function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.sin_gestionar;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
      {meta.label}
    </span>
  );
}


export default function ReferralsTab({ onOpenStudent }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], summary: null });
  const [filters, setFilters] = useState({ status: "", q: "", date_from: "", date_to: "", referent_student_id: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const { data } = await api.get(`/referrals${params.toString() ? `?${params.toString()}` : ""}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo cargar el panel"));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => setFilters({ status: "", q: "", date_from: "", date_to: "", referent_student_id: "" });

  const exportCsv = () => {
    const rows = [
      ["Alumno matriculado", "Cod. Expediente", "Referente", "Teléfono", "Tipo", "Importe", "Estado", "F. creación", "F. confirmación", "F. pago", "Pagado por", "Bizum"],
      ...data.items.map((it) => [
        it.student_display?.name || "",
        it.student_display?.codigo_expediente || "",
        it.referent_name || "",
        it.referent_phone || "",
        it.referent_type || "",
        (it.status === "pagado" ? it.amount_paid : it.amount) || 0,
        STATUS_META[it.status]?.label || it.status,
        (it.created_at || "").slice(0, 10),
        (it.fecha_confirmacion || "").slice(0, 10),
        (it.fecha_pago || "").slice(0, 10),
        it.paid_by || "",
        it.bizum_number || "",
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `referidos_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summary = data.summary || {};
  const activeFilters = useMemo(
    () => Object.values(filters).filter((v) => v).length,
    [filters],
  );

  return (
    <div className="space-y-5" data-testid="referrals-tab">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-xl bg-orange-500 text-white flex items-center justify-center">
          <Users2 className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold leading-tight">Programa de Referidos</h2>
          <p className="text-xs text-neutral-500">Gestión completa de incentivos, confirmaciones Bizum y pagos.</p>
        </div>
        <button
          onClick={exportCsv}
          data-testid="referrals-export-csv"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-neutral-300 bg-white text-neutral-700 text-xs font-semibold hover:bg-neutral-50"
        >
          <Download className="h-3.5 w-3.5" /> CSV
        </button>
      </div>

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard testid="kpi-pending" icon={Wallet} label="Pendientes" value={summary.total_pending || 0}
                   sub={`${(summary.total_pending_amount || 0).toFixed(2)} € por pagar`} color="text-red-700" />
          <KpiCard testid="kpi-paid-month" icon={TrendingUp} label="Pagados este mes" value={summary.paid_this_month || 0}
                   sub={`${(summary.paid_this_month_amount || 0).toFixed(2)} €`} color="text-emerald-700" />
          <KpiCard testid="kpi-new-month" icon={CalendarDays} label="Nuevos este mes" value={summary.referred_this_month || 0} />
          <KpiCard testid="kpi-alerts" icon={AlertTriangle} label="Alertas" value={summary.alerts || 0}
                   sub=">7 días pendiente" color={summary.alerts > 0 ? "text-red-700" : "text-neutral-900"} />
          <KpiCard testid="kpi-total" icon={Users2} label="Registros totales"
                   value={Object.values(summary.counts_by_status || {}).reduce((a, b) => a + b, 0)} />
        </div>
      )}

      {/* Filters */}
      <div className="rounded-xl border border-neutral-200 bg-white p-3 space-y-3" data-testid="referrals-filters">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-neutral-600" />
          <h3 className="text-sm font-semibold">Filtros</h3>
          {activeFilters > 0 && (
            <button onClick={clearFilters} className="ml-auto text-[11px] text-neutral-500 hover:text-orange-600 inline-flex items-center gap-1">
              <X className="h-3 w-3" /> Limpiar ({activeFilters})
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            data-testid="filter-status"
            className="h-9 px-2 rounded-lg border border-neutral-300 bg-white text-sm"
          >
            <option value="">Todos los estados</option>
            {(data.status_order || []).map((s) => (
              <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>
            ))}
          </select>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o teléfono referente…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              data-testid="filter-q"
              className="h-9 pl-7 pr-2 rounded-lg border border-neutral-300 bg-white text-sm w-full"
            />
          </div>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            data-testid="filter-date-from"
            className="h-9 px-2 rounded-lg border border-neutral-300 bg-white text-sm"
            title="Fecha desde"
          />
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            data-testid="filter-date-to"
            className="h-9 px-2 rounded-lg border border-neutral-300 bg-white text-sm"
            title="Fecha hasta"
          />
        </div>
      </div>

      {/* Table + Ranking */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Table (2/3) */}
        <div className="lg:col-span-2 rounded-xl border border-neutral-200 bg-white overflow-hidden" data-testid="referrals-table">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-500 mx-auto" />
            </div>
          ) : data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-500">Sin resultados con los filtros actuales.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed">
                <colgroup>
                  <col />
                  <col style={{ width: "220px" }} />
                  <col style={{ width: "100px" }} />
                  <col style={{ width: "140px" }} />
                  <col style={{ width: "180px" }} />
                </colgroup>
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
                    <th className="text-left px-4 py-3 font-bold">Alumno matriculado</th>
                    <th className="text-left px-4 py-3 font-bold">Referente</th>
                    <th className="text-right px-4 py-3 font-bold">€</th>
                    <th className="text-left px-4 py-3 font-bold">Estado</th>
                    <th className="text-left px-4 py-3 font-bold">Fechas</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr
                      key={it.id}
                      className={`border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer h-[56px] ${it.is_alerting ? "bg-red-50/50" : ""}`}
                      onClick={() => onOpenStudent && onOpenStudent(it.student_id)}
                      data-testid={`referral-row-${it.id}`}
                    >
                      <td className="px-4 py-3 align-middle">
                        <div className="font-semibold truncate" title={it.student_display?.name || ""}>{it.student_display?.name || "—"}</div>
                        {it.student_display?.codigo_expediente && (
                          <div className="text-[10px] font-mono text-neutral-400 truncate">{it.student_display.codigo_expediente}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="font-medium truncate" title={it.referent_name}>{it.referent_name}</div>
                        <div className="text-[10px] text-neutral-500 truncate">{it.referent_phone}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold align-middle whitespace-nowrap">
                        {((it.status === "pagado" ? it.amount_paid : it.amount) || 0).toFixed(2)} €
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <StatusPill status={it.status} />
                        {it.is_alerting && (
                          <div className="text-[10px] text-red-700 mt-0.5 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> +{it.days_in_status}d
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[11px] text-neutral-600 align-middle">
                        <div>📅 {(it.created_at || "").slice(0, 10)}</div>
                        {it.fecha_pago && <div className="text-emerald-700">💸 {it.fecha_pago.slice(0, 10)}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Ranking (1/3) */}
        <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden" data-testid="referrals-ranking">
          <div className="bg-neutral-50 border-b border-neutral-200 px-3 py-2 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-orange-500" />
            <h3 className="text-sm font-bold">Ranking de referentes</h3>
          </div>
          {(!summary.ranking || summary.ranking.length === 0) ? (
            <div className="p-6 text-xs text-neutral-500 text-center">Sin datos aún.</div>
          ) : (
            <ol className="divide-y divide-neutral-100">
              {(summary.ranking || []).map((r, i) => (
                <li
                  key={r.referent_student_id || r.referent_phone || i}
                  className="px-3 py-2 flex items-center gap-2 hover:bg-neutral-50 cursor-pointer"
                  onClick={() => r.referent_student_id && setFilters({ ...filters, referent_student_id: r.referent_student_id })}
                  data-testid={`referrals-ranking-${i}`}
                >
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                    i === 0 ? "bg-orange-500 text-white" :
                    i === 1 ? "bg-neutral-400 text-white" :
                    i === 2 ? "bg-amber-600 text-white" :
                    "bg-neutral-100 text-neutral-500"
                  }`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{r.referent_name || "—"}</div>
                    <div className="text-[10px] text-neutral-500 truncate">
                      {r.count} referidos · {r.amount_paid_total.toFixed(2)} € pagados
                      {r.pending_count > 0 && <span className="text-red-700"> · {r.pending_count} pendientes</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
